import {
  ListCalendarViewToggle,
  ViewMode,
} from '@molecules/ListCalendarViewToggle';
import {
  ProfileViewMode,
  ProfileViewToggle,
} from '@molecules/ProfileViewToggle';
import { TicketsCalendarDisplay } from '@organisms/TicketsCalendarDisplay';
import UserTicketsList from '@organisms/UserTicketsList';
import { TemporalRelation } from '@sunrise-ui/api/events';
import {
  Colors,
  TextBold,
  useDynamicStyles,
  useSafeLocalSearchParams,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';

type HeaderProps = {
  temporalMode: ProfileViewMode;
  onTemporalModeChange: (mode: ProfileViewMode) => void;
};

function TicketsHeader({ temporalMode, onTemporalModeChange }: HeaderProps) {
  const styles = useDynamicStyles(createStyles);

  return (
    <View style={styles.header}>
      <TextBold style={styles.title}>My Tickets</TextBold>
      <View style={styles.controlsRow}>
        <ProfileViewToggle
          value={temporalMode}
          onValueChange={onTemporalModeChange}
        />
        <ListCalendarViewToggle />
      </View>
    </View>
  );
}

type Props = {
  userId?: string;
};

export default function TicketsView({ userId }: Props) {
  const styles = useDynamicStyles(createStyles);
  const { view = ViewMode.LIST } = useSafeLocalSearchParams<{
    view?: ViewMode;
  }>();
  const [temporalMode, setTemporalMode] = useState<ProfileViewMode>(
    ProfileViewMode.Upcoming,
  );

  const temporalRelation =
    temporalMode === ProfileViewMode.Past
      ? TemporalRelation.Past
      : TemporalRelation.Future;

  const allowPastSelection = temporalMode === ProfileViewMode.Past;

  if (view === ViewMode.CALENDAR) {
    return (
      <View style={styles.container}>
        <TicketsHeader
          temporalMode={temporalMode}
          onTemporalModeChange={setTemporalMode}
        />
        <TicketsCalendarDisplay
          userId={userId}
          temporalRelation={temporalRelation}
          allowPastSelection={allowPastSelection}
        />
      </View>
    );
  }

  return (
    <UserTicketsList
      userId={userId}
      ListHeaderComponent={
        <TicketsHeader
          temporalMode={temporalMode}
          onTemporalModeChange={setTemporalMode}
        />
      }
      temporalRelation={temporalRelation}
    />
  );
}

const createStyles = ({ isMobile, isTablet }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: Colors.purple0,
    },
    header: {
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: isMobile ? 16 : isTablet ? 20 : 24,
      paddingTop: isMobile ? 12 : 16,
      paddingBottom: 8,
      backgroundColor: Colors.purple0,
    },
    title: {
      fontSize: isMobile ? 24 : 32,
    },
    controlsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
  });
